import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff, Lock, User, ShoppingBag, Leaf } from 'lucide-react'
import { api } from '@/lib/api'
import StoreFooter from '@/components/store/StoreFooter'

export default function AdminLoginPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const response = await api.auth.login({ email, password })

      if (response.success && response.data.user.role === 'admin') {
        localStorage.setItem('srisai_access_token', response.data.access_token)
        localStorage.setItem('srisai_admin_token', 'authenticated')
        localStorage.setItem('srisai_admin_user', JSON.stringify({
          name: `${response.data.user.first_name} ${response.data.user.last_name}`,
          role: response.data.user.role,
          email: response.data.user.email
        }))
        navigate('/admin')
        return
      }
    } catch {
      // Fallback to static login
    }

    const isStaticAdmin = (email === 'admin' && password === 'admin') ||
      (email === 'admin@srisaigrocery.com' && password === 'admin123')

    if (isStaticAdmin) {
      localStorage.setItem('srisai_access_token', 'static-admin')
      localStorage.setItem('srisai_admin_token', 'authenticated')
      localStorage.setItem('srisai_admin_user', JSON.stringify({
        name: 'Administrator',
        role: 'admin',
        email: 'admin@srisaigrocery.com'
      }))
      navigate('/admin')
    } else {
      setError('Invalid credentials. Try admin@srisaigrocery.com / admin123')
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 via-primary-500 to-accent-400 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-300/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <ShoppingBag className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-4xl font-display font-bold">Sri Sai</h1>
              <p className="text-xl font-light text-white/80">Admin Portal</p>
            </div>
          </div>
          <h2 className="text-3xl font-semibold mb-4">Welcome to Admin Portal</h2>
          <p className="text-lg text-white/80 mb-8 max-w-md">
            Manage products, track sales, and grow your business.
          </p>
          <div className="space-y-4">
            {['Product Management', 'Sales Analytics', 'Inventory Tracking', 'Customer Insights'].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <Leaf className="w-4 h-4" />
                </div>
                <span className="text-white/90">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex w-full flex-1 items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 p-8 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ShoppingBag className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-gray-800">Sri Sai</h1>
              <p className="text-sm text-gray-500">Admin Portal</p>
            </div>
          </div>

          <div className="card p-8 animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="mb-2 text-2xl font-bold text-gray-800">Admin Login</h2>
              <p className="text-gray-500">Staff only. Enter credentials to access the dashboard.</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Email / Username
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-12"
                    placeholder="Enter email or username"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-12 pr-12"
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Sign In
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 rounded-xl border border-primary-100 bg-primary-50 p-4">
              <p className="text-center text-sm text-primary-700">
                <strong>Demo:</strong> admin@srisaigrocery.com / admin123
              </p>
            </div>
          </div>

          <p className="text-center mt-6">
            <Link to="/" className="text-sm text-primary-600 hover:underline">
              ← Back to Store
            </Link>
          </p>
        </div>
      </div>
      </div>

      <div className="shrink-0 border-t border-primary-100/70 bg-gradient-to-br from-amber-50 to-orange-50 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <StoreFooter detailsClassName="mt-0" />
        </div>
      </div>
    </div>
  )
}
